/**
 * Borough Heat online protocol — message catalogue shared by client and server.
 *
 * Wire format (see codec.ts): one WebSocket text frame = one JSON array `[type, payload]` or
 * `[type, payload, rid]`. `rid` (request id) is optional; when the client sets it, the server answers
 * with exactly one `res` frame carrying the same rid.
 *
 * EXTENDING: feature teams add message types WITHOUT editing this file — create
 * `shared/messages/<team>.ts` with
 *
 *   declare module '../protocol' {
 *     interface ClientMessageMap { gangCreate: { name: string; tag: string } }
 *     interface ServerMessageMap { gangUpdate: GangView }
 *   }
 *
 * and export the payload schemas from there; the server registers a handler per client type in the
 * handler registry (server/core/handlers.ts). Unknown types are rejected server-side.
 */
import { s, type Infer } from './schema';
import type { WireSnapshot } from './snapshot';

/**
 * Bumped on any incompatible wire change. The server rejects mismatching `hello`s (kick `protocol`).
 * v3: player flags carry a public action cue (bits 16..20, see PlayerAction in snapshot.ts).
 * v4: weapons online — held weapon + aim bit in player flags (bits 21..24), cues fire/reload/swing,
 *     `wpn*` messages and the `wfx` shot broadcast (shared/messages/weapons.ts).
 * v5: server-run police — foot officers are snapshot entities of kind 2, crewed unit cars carry
 *     VehicleFlag.unit, `heat` carries the arrest meter and the armed-response stage, police shots
 *     travel as `pfx` (shared/messages/police.ts).
 * v6: server-authoritative collisions — `vhit` (a collision the server resolved for the car you drive),
 *     `knock` (you were run over), `mv.k` (last `vhit` applied; an unacknowledged hit is forced).
 * v7: production properties — `prop*` messages and the `props` push (shared/messages/properties.ts),
 *     rumour boards may carry `whispers`.
 * v8: gang businesses — gangBusiness messages, PropView.gang / can / gangAccess / storeItems,
 *     `propertyRaided.ownerType`.
 * v9: `PropView.takeOut` (gang premises: stock leaves the store only with inventory.manage).
 * v10: deeper markets (roadmap P3) — `marketBoard` request, `marketNews` push, optional `wants` / `intro`
 *      fields (docs/MARKETS.md).
 * v11: placement acknowledgement — `corr.n` numbers a server placement, `mv.c` acknowledges it, so moves
 *      sent before a teleport aren't judged against the new spot (docs/SECURITY.md MOVE-41); officers can
 *      be hit (docs/POLICE.md §10).
 * v12: territory (roadmap P4) — `territoryBoard` request, `territory` / `territoryNews` pushes
 *      (docs/TERRITORY.md).
 * v13: dynamic world events (roadmap P5) — event board / news / updates (shared/messages/events.ts,
 *      docs/EVENTS.md).
 * v14: crew mates on the map — the `crewPos` push (shared/messages/social.ts, docs/ONLINE-UI-GANGS.md).
 * v15: everybody on the map — the `playersPos` push (every other player in the world on your shard, every
 *      2 s, with their relation to you: crew / gang / other; shared/messages/social.ts).
 * v16: Pike's spray bay — `vehicleMod` (rims, rim colour, neon, ride height, finish, camber, track, keep the
 *      car; shared/messages/economy.ts, priced and checked by the server with shared/garage.ts) and the
 *      vehicle metadata's `mod` field, so every player sees the look (docs/ECONOMY.md "Garage").
 * v17: the Kessmouth week (WP-X5) — the `weekBoard` request and the `week` / `weekNews` pushes: the weekly
 *      modifier, your firm's week so far and the honours kept for ever (shared/messages/week.ts, docs/WEEK.md).
 * v18: Lenny's list (WP-X8) — the `lennyTake` request and `characterRecord`'s `lenny` field: three optional
 *      errands a day, settled by the server (shared/messages/lenny.ts, docs/CONTACTS.md "Lenny's list").
 * v19: the hideout (WP-X4) — `hideoutView` / `hideoutBuy` / `hideoutStation` / `hideoutEnter` / `hideoutLivery`
 *      and the `hideout` push (shared/messages/hideout.ts, docs/HIDEOUT.md); the gang permission
 *      `hideout.furnish`; the vehicle metadata's mods gain `l` (the gang livery's colour on the car's trim).
 * v20: the firm's jobs (WP-X3) — `firmPlan` / `firmClaim` / `firmGo` / `firmDeliver` / `firmState` / `firmCancel` and the
 *      `firmJob` / `firmIntel` pushes: co-op jobs with roles, the Savings Bank job first (shared/messages/crewJobs.ts,
 *      docs/ACTIVITIES.md "The firm's jobs"); the week's `jobs` honour.
 * v21: three new cars (the Corran Drover Estate `estate`, the Ashcombe Grandee `exec`, the Vesper Linnet `roadster`):
 *      new vehicle spec wire ids 7–9 (shared/limits.ts VEHICLE_SPEC_IDS), which an older client can't draw.
 * v23: the shard owns the clock and the weather — the `clock` push (the hour at a server time and the weather;
 *      on join / resume, every 60 s and when the weather turns; shared/clock.ts, shared/messages/clock.ts).
 *      (v22 is taken by the police-ai branch.)
 */
export const PROTOCOL_VERSION = 23;

// ----------------------------------------------------------------------------- client → server

const coord = s.num({ min: -2000, max: 2000 });
const vel = s.num({ min: -250, max: 250 });
const angle = s.num({ min: -1000, max: 1000 });

export const CoreClientSchemas = {
  /** First frame on every socket. Session token comes from POST /api/login (never in the URL). */
  hello: s.obj({ p: s.int({ min: 0, max: 1_000_000 }), token: s.str({ min: 16, max: 128 }), shard: s.opt(s.str({ max: 64 })) }),
  /**
   * Movement proposal (client-simulated, server-validated). `s` is a strictly increasing sequence
   * number; `v` is the entity id of the vehicle being driven, 0 on foot. `k` (protocol v5): the last
   * `vhit` sequence number this client has applied (omitted until the first one). `c`: the last
   * placement (`corr.n`) this client has applied, 0 before any; a client that sends it has its moves
   * from before a server placement (still in flight when it was placed) ignored instead of judged
   * against the new spot. Optional for now (older clients omit it); a protocol bump makes it required.
   */
  mv: s.obj({ s: s.int({ min: 0 }), x: coord, z: coord, h: angle, vx: vel, vz: vel, v: s.int({ min: 0 }), k: s.opt(s.int({ min: 0 })), c: s.opt(s.int({ min: 0 })) }),
  /** Get into a networked vehicle (seat 0 = driver). */
  enter: s.obj({ v: s.int({ min: 1 }), seat: s.opt(s.int({ min: 0, max: 7 })) }),
  /** Get out at the proposed kerbside position (validated against the vehicle position). */
  exit: s.obj({ x: coord, z: coord, h: angle }),
  /**
   * "I just took this ambient (client-local) car": the server materialises it as a networked vehicle
   * with the player as driver. Rate limited; range-checked; spec must exist.
   */
  claim: s.obj({ spec: s.str({ max: 32 }), paint: s.int({ min: 0, max: 0xffffff }), x: coord, z: coord, h: angle }),
  /**
   * Force a LOCKED vehicle you have no key for. Takes BREAK_IN_MS standing next to it (cancelled if
   * you walk away); then you are in the driver's seat and the car is stolen. Noisy: a crime.
   */
  breakin: s.obj({ v: s.int({ min: 1 }) }),
  /** Lock / unlock a vehicle you are authorised for (owner, gang rule). */
  lock: s.obj({ v: s.int({ min: 1 }), locked: s.bool() }),
  /**
   * Driver-reported collision damage to the vehicle being driven. The server only ever LOWERS
   * health from client reports (bounded per report and per second); repairs are server actions.
   */
  vdmg: s.obj({ v: s.int({ min: 1 }), amt: s.num({ min: 0, max: 1000 }) }),
  ping: s.obj({ c: s.num() }),
  /** Voluntary quit. Removal is immediate only when nothing is at stake (see docs/ONLINE.md §6). */
  bye: s.empty(),
};

type CoreClient = { [K in keyof typeof CoreClientSchemas]: Infer<(typeof CoreClientSchemas)[K]> };

// Interfaces (not type aliases) so feature teams can extend them by declaration merging.
export interface ClientMessageMap extends CoreClient {}

// ----------------------------------------------------------------------------- server → client

export interface RosterEntry {
  /** Shard-local entity id of the character's avatar. */
  eid: number;
  /** Persistent character id. */
  cid: number;
  name: string;
  /** Disconnected but still in the world (grace window). */
  lingering?: boolean;
}

export interface WelcomeMsg {
  p: number;
  shard: { id: string; name: string };
  you: { eid: number; cid: number; name: string };
  /** True when this connection re-attached to a character still lingering in the world. */
  resumed: boolean;
  tick: number;
  /** Server wall clock (ms) for time sync. */
  st: number;
  tickHz: number;
  pos: { x: number; z: number; h: number };
  /**
   * Authoritative velocity (m/s). On resume the character's car came to rest while lingering, so
   * this is usually 0: the client adopts it with `pos`.
   */
  vel: { vx: number; vz: number };
  /** Vehicle entity id the character sits in (resume), 0 when on foot. */
  vehicle: number;
  /** Seat in that vehicle (0 = driver), -1 on foot. */
  seat: number;
  roster: RosterEntry[];
}

export interface CorrectionMsg {
  /**
   * Last accepted movement sequence. The client rewinds to this authoritative state — position,
   * heading AND velocity (`vx`/`vz`) — and replays/continues from there. Adopting the velocity is
   * required: the server checks acceleration against it.
   */
  s: number;
  x: number;
  z: number;
  h: number;
  vx: number;
  vz: number;
  v: number;
  reason: string;
  /**
   * Set when the SERVER placed you (a teleport, a respawn, an exit spot, a staged move): the placement
   * number, increasing per character. Acknowledge it with `mv.c` once applied; until then the server
   * ignores your moves (they were sent from where you were before) and repeats the placement now and then.
   */
  n?: number;
}

/**
 * A collision the server resolved for the car you drive (protocol v5). `n` increases per character;
 * acknowledge it with `mv.k`. `x/z/vx/vz` are the server's post-impact state of your car, `dvx/dvz` the
 * velocity change it gave you, `px/pz` the positional push, `o` the other party (vehicle entity id),
 * `dmg` the damage it applied to your car (server health, never a heal).
 */
export interface VehicleHitMsg {
  n: number;
  v: number;
  o: number;
  x: number;
  z: number;
  vx: number;
  vz: number;
  dvx: number;
  dvz: number;
  px: number;
  pz: number;
  dmg: number;
}

/** You were run over (protocol v5): down for `ms`, by the driver with character id `by`. */
export interface KnockMsg {
  ms: number;
  by: number;
  /** Direction the car was going (unit vector) — where you fall. */
  dx: number;
  dz: number;
}

export interface ResponseMsg {
  ok: boolean;
  data?: unknown;
  error?: { code: string; message: string };
}

export type KickReason = 'protocol' | 'auth' | 'replaced' | 'full' | 'cheat' | 'flood' | 'shutdown' | 'timeout' | 'admin';

export interface ServerMessageMap {
  welcome: WelcomeMsg;
  snap: WireSnapshot;
  corr: CorrectionMsg;
  vhit: VehicleHitMsg;
  knock: KnockMsg;
  res: ResponseMsg;
  pong: { c: number; s: number };
  notice: { code: string; text: string };
  kick: { reason: KickReason; text?: string };
  roster: { add?: RosterEntry[]; del?: number[] };
}

export type ClientType = keyof ClientMessageMap & string;
export type ServerType = keyof ServerMessageMap & string;

/** Error codes used in `res.error.code` by core handlers (feature teams may add their own). */
export const ErrorCodes = {
  invalid: 'invalid',
  unknownType: 'unknown_type',
  rateLimited: 'rate_limited',
  notFound: 'not_found',
  forbidden: 'forbidden',
  tooFar: 'too_far',
  busy: 'busy',
  insufficientFunds: 'insufficient_funds',
  internal: 'internal',
} as const;
